import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select, MultiSelect } from '..';
import { FiGift, FiFileText, FiBriefcase } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';

export default function BenefitForm({ companyId, companies = [], initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    value: '',
    frequency: 'monthly',
    company_id: '',
  });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

  const companyOptions = useMemo(() => {
    const options = companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
    return [{ value: '', label: 'SELECIONE UMA EMPRESA...' }, ...options];
  }, [companies]);

  function formatCurrencyForDisplay(raw) {
    if (raw === undefined || raw === null || raw === '') {
      return '';
    }

    let value = String(raw).replace(/[^\d,.-]/g, '').replace(',', '.');
    const num = Number(value);

    if (Number.isNaN(num)) {
      return '';
    }

    const fixed = num.toFixed(2).replace('.', ',');
    const parts = fixed.split(',');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const decimalPart = parts[1];

    return `R$ ${integerPart},${decimalPart}`;
  }

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        value: initialData.value != null ? formatCurrencyForDisplay(initialData.value) : '',
        frequency: initialData.frequency || 'monthly',
        company_id: initialData.company_id ? String(initialData.company_id) : (initialData.company ? String(initialData.company.id) : ''),
      });
      const initialCompanyId = initialData.company_id || initialData.company?.id || companyId;
      setSelectedCompanyIds(initialCompanyId ? [String(initialCompanyId)] : []);
    }
  }, [initialData]);

  useEffect(() => {
    if (!isEdit) {
      const defaultCompanyId = companyId ? String(companyId) : '';
      setSelectedCompanyIds(defaultCompanyId ? [defaultCompanyId] : []);
    }
  }, [companyId, isEdit]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  function validateForm() {
    const messages = [];

    if (!formData.name || !formData.name.trim()) {
      messages.push('Informe o nome do benefício.');
    }

    if (formData.value === undefined || formData.value === null || String(formData.value).trim() === '') {
      messages.push('Informe o valor do benefício.');
    }

    if (messages.length > 0) {
      messages.forEach(message => toast.error(message));
      return false;
    }

    return true;
  }

  function normalizeValue(raw) {
    if (raw == null) {
      return '';
    }

    let value = String(raw).trim();
    if (!value) {
      return '';
    }

    value = value.replace(/[R$\s]/g, '');
    const parts = value.split(',');

    if (parts.length === 1) {
      return parts[0].replace(/\./g, '');
    }

    const integerPart = parts[0].replace(/\./g, '');
    const decimalPart = parts[1];

    return `${integerPart}.${decimalPart}`;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const normalizedValue = normalizeValue(formData.value);

      if (isEdit && initialData && initialData.id) {
        const selectedCompanyId = formData.company_id || companyId;
        if (!selectedCompanyId) {
          toast.error('Selecione uma empresa para cadastrar benefícios.');
          setLoading(false);
          return;
        }
        const payload = {
          name: formData.name,
          description: formData.description || null,
          value: normalizedValue,
          frequency: formData.frequency,
          company_id: selectedCompanyId,
        };
        const originalCompanyId = initialData.company?.id || initialData.company_id || companyId;
        const result = await Api.updateBenefit(originalCompanyId, initialData.id, payload);
        if (result && !result.error) {
          if (onSuccess) {
            onSuccess(result);
          }
        } else {
          const message = result && result.error ? result.error : 'Erro ao salvar benefício.';
          setError(message);
          toast.error(message);
        }
      } else {
        const cleanedCompanyIds = (selectedCompanyIds || []).filter(id => id !== 'all');
        if (cleanedCompanyIds.length === 0) {
          toast.error('Selecione ao menos uma empresa.');
          setLoading(false);
          return;
        }
        for (const companyIdToCreate of cleanedCompanyIds) {
          const payload = {
            name: formData.name,
            description: formData.description || null,
            value: normalizedValue,
            frequency: formData.frequency,
            company_id: companyIdToCreate,
          };
          const created = await Api.createBenefit(companyIdToCreate, payload);
          if (created && created.error) {
            const message = created.error || 'Erro ao salvar benefício.';
            setError(message);
            toast.error(message);
            break;
          }
        }
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (err) {
      setError('Erro ao salvar benefício. Tente novamente.');
      toast.error('Erro ao salvar benefício. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>Dados do benefício</h3>
        <div className="form-grid">
          {isEdit ? (
            <Select
              id="company_id"
              label="Empresa *"
              placeholder="SELECIONE UMA EMPRESA..."
              value={formData.company_id}
              onChange={handleChange}
              icon={FiBriefcase}
              options={companyOptions}
              searchable
            />
          ) : (
            <MultiSelect
              id="companyIds"
              label="Empresas *"
              value={selectedCompanyIds}
              onChange={(newValues) => {
                const allValue = 'all';
                const currentSelected = selectedCompanyIds;
                const isAllSelected = newValues.includes(allValue);
                const wasAllSelected = currentSelected.includes(allValue);
                let nextValues;
                if (isAllSelected && !wasAllSelected) {
                  const allIds = companies.map(c => String(c.id));
                  nextValues = [allValue, ...allIds];
                } else if (!isAllSelected && wasAllSelected) {
                  nextValues = [];
                } else if (wasAllSelected && newValues.length < currentSelected.length) {
                  const remaining = newValues.filter(v => v !== allValue);
                  nextValues = remaining;
                } else {
                  const specificIds = newValues.filter(v => v !== allValue);
                  const allIds = companies.map(c => String(c.id));
                  if (specificIds.length === allIds.length && allIds.length > 0) {
                    nextValues = [allValue, ...specificIds];
                  } else {
                    nextValues = specificIds;
                  }
                }
                setSelectedCompanyIds(nextValues);
              }}
              icon={FiBriefcase}
              options={[
                { value: 'all', label: 'TODAS' },
                ...companies.map(c => ({
                  value: String(c.id),
                  label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
                })),
              ]}
              searchable
            />
          )}
          <Input
            id="name"
            label="Nome do Benefício *"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ex: Vale Alimentação"
            required
            icon={FiGift}
          />
          <Select
            id="frequency"
            label="Frequência *"
            value={formData.frequency}
            onChange={handleChange}
            options={[
              { value: 'monthly', label: 'Mensal' },
              { value: 'daily', label: 'Diário' }
            ]}
          />
          <Input
            id="value"
            label="Valor do Benefício *"
            value={formData.value}
            onChange={handleChange}
            placeholder="Ex: R$ 32,00"
            mask="currency"
          />
        </div>

        <h3 className="section-title">Descrição</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="description"
              label="Descrição do Benefício"
              value={formData.description}
              onChange={handleChange}
              placeholder="Informe detalhes sobre o benefício"
              icon={FiFileText}
            />
          </div>
        </div>

        {error && <div className="form-error">{error}</div>}
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : isEdit ? 'Salvar' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
